import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, USER_COMMANDS } from 'cpool-constants';
import type { Schema_User } from 'cpool-schemas';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

type DeleteAbsenceMutationProps = {
    eventId?: string;
    resetInputs: () => void;
};

export const useDeleteAdviserAbsenceMutation = ({ eventId, resetInputs }: DeleteAbsenceMutationProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ userId }: { userId?: string }) => {
            return APICommunicator.sendCommand({
                aggregateId: userId,
                aggregateName: AGGREGATE_NAMES.USER,
                commandName: USER_COMMANDS.deleteAdviserAbsence,
                payload: {
                    eventId,
                },
            });
        },
        onError: () => {
            toast.error(t('DASHBOARD.ADVISER_ABSENCE.ERROR'));
        },
        onMutate: async ({ userId }) => {
            queryClient.setQueryData(queryKeys.getUser(userId), (data: Schema_User) => {
                data.absence.pop();
                return data;
            });
        },
        onSettled: async (data, _, { userId }) => {
            if (data) setQueryPosition(JSON.stringify(queryKeys.getUser(userId)), data.position);
            await queryClient.invalidateQueries({ queryKey: queryKeys.getUser(userId) });
        },
        onSuccess: () => {
            resetInputs();
            toast.success(t('DASHBOARD.ADVISER_ABSENCE.DELETED'));
        },
    });
};
